import React from 'react'
import PropTypes from 'prop-types'
import './QuickActions.scss'

/**
 * QuickActions - Grid of quick action buttons
 *
 * @param {Array} actions - Array of action objects
 * @param {string} title - Widget title
 * @param {number} columns - Number of grid columns (2, 3, or 4)
 * @param {boolean} compact - Compact display mode
 * @param {boolean} loading - Show loading state
 */
const QuickActions = ({
	actions = [],
	title = 'Quick Actions',
	columns = 3,
	compact = false,
	loading = false
}) => {
	const getDefaultIcon = (type) => {
		const icons = {
			create: (
				<svg viewBox="0 0 24 24" fill="currentColor">
					<path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
				</svg>
			),
			view: (
				<svg viewBox="0 0 24 24" fill="currentColor">
					<path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
				</svg>
			),
			edit: (
				<svg viewBox="0 0 24 24" fill="currentColor">
					<path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
				</svg>
			),
			download: (
				<svg viewBox="0 0 24 24" fill="currentColor">
					<path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
				</svg>
			),
			upload: (
				<svg viewBox="0 0 24 24" fill="currentColor">
					<path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z" />
				</svg>
			),
			settings: (
				<svg viewBox="0 0 24 24" fill="currentColor">
					<path d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
				</svg>
			),
			report: (
				<svg viewBox="0 0 24 24" fill="currentColor">
					<path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z" />
				</svg>
			),
			user: (
				<svg viewBox="0 0 24 24" fill="currentColor">
					<path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
				</svg>
			),
			default: (
				<svg viewBox="0 0 24 24" fill="currentColor">
					<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
				</svg>
			)
		}
		return icons[type] || icons.default
	}

	if (loading) {
		return (
			<div className="quick-actions">
				<div className="quick-actions__header">
					<h3 className="quick-actions__title">{title}</h3>
				</div>
				<div className={`quick-actions__grid quick-actions__grid--${columns}`}>
					{[...Array(6)].map((_, i) => (
						<div key={i} className="quick-actions__item quick-actions__item--skeleton">
							<div className="skeleton skeleton--icon"></div>
							<div className="skeleton skeleton--label"></div>
						</div>
					))}
				</div>
			</div>
		)
	}

	return (
		<div className={`quick-actions ${compact ? 'quick-actions--compact' : ''}`}>
			{title && (
				<div className="quick-actions__header">
					<h3 className="quick-actions__title">{title}</h3>
				</div>
			)}

			{actions.length === 0 ? (
				<div className="quick-actions__empty">
					<p>No quick actions available</p>
				</div>
			) : (
				<div className={`quick-actions__grid quick-actions__grid--${columns}`}>
					{actions.map((action, index) => (
						<button
							key={action.id || index}
							className={`quick-actions__item ${action.variant ? `quick-actions__item--${action.variant}` : ''} ${action.disabled ? 'quick-actions__item--disabled' : ''}`}
							onClick={action.onClick}
							disabled={action.disabled}
							title={action.description || action.label}
						>
							<span className="quick-actions__icon">
								{action.icon || getDefaultIcon(action.type)}
							</span>
							<span className="quick-actions__label">{action.label}</span>
							{action.badge && (
								<span className="quick-actions__badge">{action.badge}</span>
							)}
						</button>
					))}
				</div>
			)}
		</div>
	)
}

QuickActions.propTypes = {
	actions: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			label: PropTypes.string.isRequired,
			description: PropTypes.string,
			icon: PropTypes.node,
			type: PropTypes.string,
			variant: PropTypes.oneOf(['primary', 'success', 'warning', 'danger', 'info']),
			onClick: PropTypes.func,
			disabled: PropTypes.bool,
			badge: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
		})
	),
	title: PropTypes.string,
	columns: PropTypes.oneOf([2, 3, 4, 5]),
	compact: PropTypes.bool,
	loading: PropTypes.bool
}

export default QuickActions
